import { Badge } from '@artic/ui/badge';
import type { QueryClient } from '@tanstack/react-query';
import type { AnyRouteMatch } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { EditProjectButton } from './edit-project-button';
import { ProjectStateChip } from './project-state-chip';
import { ProjectViewToggle } from './project-view-toggle';
import { projectPlanningDetailQueryOptions } from './queries';
import type { ProjectPlanningDetail } from './types';
import { UnarchiveProjectButton } from './unarchive-project-button';

/**
 * ENG-720 Band 1. The Project view's top row is the application header itself:
 * `Projects > Apple Park  ● Released  Update Project` on the left, the
 * `Grid | Timeline` toggle on the right. Both halves are declared per-route
 * through `staticData` (see `router-types.d.ts`) rather than rendered by the
 * page, which is what retired the old `ProjectPageHeader` bar.
 *
 * Every `projects/$projectId*` route is a flat, non-nested sibling, so each one
 * declares these itself. Keeping the two resolvers here means that's a one-line
 * reference per route with nothing to drift.
 *
 * Both read the project detail straight out of the query cache — the same
 * source the `breadcrumb` resolver uses — so they add no fetch. `null` while
 * the detail is still in flight; `RouteHeaderActions` / `RouteBreadcrumbs`
 * re-resolve on every query-cache event, so they fill in as soon as it lands.
 */

function readProject(match: AnyRouteMatch, queryClient: QueryClient) {
  const { projectId } = match.params as { projectId: string };
  return {
    projectId,
    project: queryClient.getQueryData(projectPlanningDetailQueryOptions(projectId).queryKey),
  };
}

function ProjectBreadcrumbBadges({ project }: { project: ProjectPlanningDetail }) {
  const { t } = useTranslation('project');
  return (
    <>
      <ProjectStateChip projectStatus={project.project_status} />
      {/* ENG-981: an archived project is read-only; say so, and offer the way back. */}
      {project.is_archived && <Badge variant="outline">{t('archive.archived')}</Badge>}
      <EditProjectButton project={project} />
      {project.is_archived && (
        <UnarchiveProjectButton project={project} variant="link" size="sm" className="h-auto p-0" />
      )}
    </>
  );
}

function ProjectHeaderActions({ projectId }: { projectId: string }) {
  return <ProjectViewToggle projectId={projectId} />;
}

/** State chip + Update Project (+ Archived / Unarchive), rendered after the final breadcrumb. */
export function projectBreadcrumbBadges(match: AnyRouteMatch, queryClient: QueryClient): ReactNode {
  const { project } = readProject(match, queryClient);
  if (!project) return null;
  return <ProjectBreadcrumbBadges project={project} />;
}

/**
 * `Grid | Timeline`, pinned to the trailing edge of the header. Declared only
 * by the two routes the toggle actually switches between — the release,
 * redraft, run-sheets and legacy gantt routes deliberately omit it, exactly as
 * they previously passed `showViewToggle={false}`.
 */
export function projectHeaderActions(match: AnyRouteMatch): ReactNode {
  const { projectId } = match.params as { projectId: string };
  if (!projectId) return null;
  return <ProjectHeaderActions projectId={projectId} />;
}
